"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerificationService = void 0;
const Helper_1 = require("../common/Helper");
class VerificationService {
    constructor(configUserProvider) {
        this.config = configUserProvider.getConfig();
        this.userManager = configUserProvider.getUserManager();
    }
    /**
     * To initiate the account verification, call **initiateAccountVerification()**. This will send verification code  email or sms or ivr based on the verificationMedium you mentioned.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/cgans5erj5alg-init-account-verification for more details.
     * @example
     * ```js
     * cidaasVerificationService.initiateAccountVerification({
     *   verificationMedium: 'email',
     *   requestId: 'your requestId',
     *   processingType: ProcessingType.Code,
     *   email: 'your email'
     * }).then(function (response) {
     *   // the response will give you account verification details.
     * }).catch(function(ex) {
     *   // your failure code here
     * });
     * ```
     */
    initiateAccountVerification(options) {
        try {
            const url = this.config.authority + "/verification-srv/account/initiate";
            const form = Helper_1.Helper.createForm(url, options);
            document.body.appendChild(form);
            form.submit();
        }
        catch (ex) {
            throw new Helper_1.CustomException(String(ex), 417);
        }
    }
    /**
     * To complete the verification, call **verifyAccount()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/r8h9mvavvw2e6-verify-account for more details.
     * @example
     * ```js
     * cidaasVerificationService.verifyAccount({
     *   accvid: 'your accvid', // which you will get on initiate account verification response
     *   code: 'your code in email or sms or ivr'
     * }).then(function (response) {
     *   // the response will give you account verification ID and unique code.
     * }).catch(function(ex) {
     *   // your failure code here
     * });
     * ```
     */
    verifyAccount(options, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/account/verify";
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, false, "POST", undefined, headers);
    }
    /**
     * To get all configured multi factor authentication, call **getMFAList()**.
     *
     * As Parameter, you need to pass request id & one of the following: email or mobile phone or username.
     *
     * In case you are in prelogin page and don't have user information yet (such as mfa-required & account-verification page), you could pass
     * request id & masked sub (one of the following: sub or q), depends on which one is available in the query parameter generated by cidaas.
     *
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/ee688a9c52b63-list-of-configured-verification-methods for more details.
     * @example
     * ```js
     * cidaasVerificationService.getMFAList({
     *   request_id: 'your request id',
     *   email: 'your email'
     * }).then(function (response) {
     *   // the response will give you list of configured multi factor authentication
     * }).catch(function(ex) {
     *   // your failure code here
     * });
     * ```
     */
    getMFAList(options, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/setup/public/configured/list";
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, false, "POST", undefined, headers);
    }
    /**
     * to cancel mfa process, call **cancelMFA()**.
     * @example
     * ```js
     * cidaasVerificationService.cancelMFA({
     *   exchange_id: 'exchange id from initiateMFA() response',
     *   reason: 'reason of mfa cancelation',
     *   type: 'authentication type e.g. email'
     * }).then(function (response) {
     *   // your success code here
     * }).catch(function(ex) {
     *   // your failure code here
     * });
     * ```
     */
    cancelMFA(options, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/authenticate/cancel/" + options.type;
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST", undefined, headers);
    }
    /**
     * To get list of all verification type configured, call **getAllVerificationList()**. access_token must be passed as function parameter.
     * @example
     * ```js
     * cidaasVerificationService.getAllVerificationList()
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    getAllVerificationList(access_token, headers) {
        const _serviceURL = `${this.config.authority}/verification-srv/config/list`;
        if (access_token) {
            return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, undefined, "GET", access_token, headers);
        }
        return Helper_1.Helper.getAccessTokenFromUserStorage(this.userManager).then((accessToken) => {
            return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, undefined, "GET", accessToken, headers);
        });
    }
    /**
     * To initiate enrollment of new multi factor authentication, call **initiateEnrollment()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/branches/master/f85aef6754714-initiate-physical-verification-setup for more details.
     * @example
     * ```js
     * const options = {
     *   verification_type: 'one of verification_type such as fido2, face, ivr',
     *   deviceInfo: {
     *    deviceId: '',
     *    location: {lat: '', lon: ''}
     *  }
     * }
     *
     * cidaasVerificationService.initiateEnrollment(options)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    initiateEnrollment(options, access_token) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/setup/initiate/" + options.verification_type;
        if (access_token) {
            return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST", access_token);
        }
        return Helper_1.Helper.getAccessTokenFromUserStorage(this.userManager).then((accessToken) => {
            return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST", accessToken);
        });
    }
    /**
     * to get the status of MFA enrollment, call **getEnrollmentStatus()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/branches/master/b06447d02d8e0-get-status-of-physical-verification-setup-configuration for more details.
     * @example
     * ```js
     * cidaasVerificationService.getEnrollmentStatus('statusId from initiateEnrollment()')
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
    */
    getEnrollmentStatus(status_id, access_token, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/notification/status/" + status_id;
        if (access_token) {
            return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, undefined, "POST", access_token, headers);
        }
        return Helper_1.Helper.getAccessTokenFromUserStorage(this.userManager).then((accessToken) => {
            return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, undefined, "POST", accessToken, headers);
        });
    }
    /**
     * to finish enrollment process of new multi factor authentication, call **enrollVerification()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/branches/master/20ec76e937b27-enroll-physical-verification-setup for more details.
     * @example
     * ```js
     * const fidoPayload = {
     *   sub: 'your sub',
     *   exchange_id: 'exchange_id from initiateEnrollment()',
     *   verification_type: 'fido2',
     *   fido2_client_response: {
     *     client_response: 'client_response from doing fido process',
     *     fidoRequestId: 'fidoRequestId from initiateEnrollment',
     *   }
     * }
     * cidaasVerificationService.enrollVerification(fidoPayload)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    enrollVerification(options) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/setup/enroll/" + options.verification_type;
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST");
    }
    /**
     * to see details of configured verification type, call **checkVerificationTypeConfigured()**.
     * @example
     * ```js
     * cidaasVerificationService.checkVerificationTypeConfigured({
     *   request_id: 'your request id',
     *   email: 'your email',
     *   verification_type: 'email'
     * }).then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    checkVerificationTypeConfigured(options) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/setup/public/configured/check/" + options.verification_type;
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST");
    }
    /**
     * to initiate multi factor auhentication, call **initiateMFA()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/2a3ea581bb249-initiate-verification for more details.
     * @example
     * ```js
     * const options = {
     *   request_id: 'your request id',
     *   usage_type: 'PASSWORDLESS_AUTHENTICATION',
     *   type: 'email'
     *   email: 'your email'
     *  }
     * }
     *
     * cidaasVerificationService.initiateMFA(options)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    initiateMFA(options, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/authenticate/initiate/" + options.type;
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, false, "POST", undefined, headers);
    }
    /**
     * to authenticate with multi factor auhentication, call **authenticateMFA()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/1aa38936252d6-perform-the-authentication-method for more details.
     * @example
     * ```js
     * cidaasVerificationService.authenticateMFA({
     *   type: 'email',
     *   client_id: 'your client id',
     *   exchange_id: exchange id from initiateMFA(),
     *   pass_code: 'code to authenticate'
     * }).then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    authenticateMFA(options, headers) {
        const _serviceURL = this.config.authority + "/verification-srv/v2/authenticate/authenticate/" + options.type;
        return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST", undefined, headers);
    }
    /**
     * to initiate verification process, call **initiateVerification**
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/odmtzu2e53f5k-initiate-verification-enrollment for more details.
     * @example
     * ```js
     * const options = {
     *   mobile_number: 'your mobile number',
     *  }
     * }
     * const trackId = 'your track id'
     * const method = 'email'
     *
     * cidaasVerificationService.initiateVerification(options, trackId, method)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    initiateVerification(options, trackId, method) {
        const serviceURL = this.config.authority + '/verification-actions-srv/setup/' + method + '/initiate/' + trackId;
        return Helper_1.Helper.createHttpPromise(options, serviceURL, undefined, 'POST');
    }
    /**
     * to finish configuring verification process, call **configureVerification**
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/vurqjgv46yyid-initiate-verification-setup-authenticate for more details.
     * @example
     * ```js
     * const options = {
     *   exchange_id: 'your exchange id';
     *	 sub: 'your sub;
     *	 pass_code: 'your pass code';
     *  }
     * }
     * const method = 'email'
     *
     * cidaasVerificationService.initiateVerification(options, method)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    configureVerification(options, method) {
        const serviceURL = this.config.authority + '/verification-actions-srv/setup/' + method + '/verification';
        return Helper_1.Helper.createHttpPromise(options, serviceURL, undefined, 'POST');
    }
    /**
     * to configure friendly name, call **configureFriendlyName**
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/f0i458vm5zo6z-add-a-friendly-name for more details.
     * @example
     * ```js
     * const options = {
     *	 sub: 'your sub;
     *	 friendly_name: 'your friendly name';
     *  }
     * }
     * const trackId = 'your track id'
     * const method = 'email'
     *
     * cidaasVerificationService.configureFriendlyName(options, trackId, method)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    configureFriendlyName(options, trackId, method) {
        const serviceURL = this.config.authority + '/verification-actions-srv/setup/users/friendlyname/' + method.toUpperCase() + '/' + trackId;
        return Helper_1.Helper.createHttpPromise(options, serviceURL, undefined, 'PUT');
    }
}
exports.VerificationService = VerificationService;
